import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData, updateData, deleteData } from '../../services/request'

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600 whitespace-nowrap">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminEmploymeUserSection = ({ userId, expectedRole, backPath, backLabel, roleLabel }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [genMsg, setGenMsg] = useState('')
  const [actioning, setActioning] = useState(false)
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')

  const fetchUser = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/users/${userId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setUser(data)
        setFirstName(data.firstName || '')
        setLastName(data.lastName || '')
        setEmail(data.email || '')
        setPhoneNumber(data.phoneNumber || '')
      } else {
        setError(res.result?.data?.message || res.result?.data?.data || `${roleLabel} not found`)
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchUser()
  }, [userId])

  const handleToggleStatus = async () => {
    const newStatus = user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'
    setActioning(true)
    setError('')
    setGenMsg('')
    const url = `${getAPIBaseURL()}/employme/admin/users/${userId}/status`
    const res = await patchData(url, { 'Content-Type': 'application/json' }, { status: newStatus })
    setActioning(false)
    if (res.error === '' && res.result?.status === 200) {
      setUser((prev) => ({ ...prev, status: newStatus }))
      setGenMsg(`User ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'} successfully`)
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update status')
    }
  }

  const handleVerifyEmail = async () => {
    if (!window.confirm('Manually verify this user\'s email address?')) return
    setActioning(true)
    setError('')
    setGenMsg('')
    const url = `${getAPIBaseURL()}/employme/admin/users/${userId}/verify-email`
    const res = await patchData(url, { 'Content-Type': 'application/json' }, {})
    setActioning(false)
    if (res.error === '' && res.result?.status === 200) {
      const data = res.result.data?.data || res.result.data
      setUser(data)
      setGenMsg('Email verified successfully')
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to verify email')
    }
  }

  const handleCancelEdit = () => {
    setEditing(false)
    setFirstName(user?.firstName || '')
    setLastName(user?.lastName || '')
    setEmail(user?.email || '')
    setPhoneNumber(user?.phoneNumber || '')
    setError('')
  }

  const handleSave = async () => {
    setError('')
    setGenMsg('')
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required')
      return
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Valid email is required')
      return
    }

    setActioning(true)
    const url = `${getAPIBaseURL()}/employme/admin/users/${userId}`
    const res = await updateData(url, { 'Content-Type': 'application/json' }, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      phoneNumber: phoneNumber.trim()
    })
    setActioning(false)

    if (res.error === '' && res.result?.status === 200) {
      const data = res.result.data?.data || res.result.data
      setUser(data)
      setFirstName(data.firstName || '')
      setLastName(data.lastName || '')
      setEmail(data.email || '')
      setPhoneNumber(data.phoneNumber || '')
      setEditing(false)
      setGenMsg('User updated successfully')
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update user')
    }
  }

  const handleDelete = async () => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email
    if (!window.confirm(`Delete ${roleLabel.toLowerCase()} "${name}"? This action cannot be undone.`)) return

    setActioning(true)
    setError('')
    setGenMsg('')
    const url = `${getAPIBaseURL()}/employme/admin/users/${userId}`
    const res = await deleteData(url, {}, {})
    setActioning(false)

    if (res.error === '' && res.result?.status === 200) {
      navigate(backPath)
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to delete user')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const fullName = () => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '—'

  if (loading) {
    return <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
  }

  if (error && !user) {
    return (
      <>
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || `${roleLabel} not found`}
        </div>
        <Link
          to={backPath}
          className="inline-block rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {backLabel}
        </Link>
      </>
    )
  }

  if (!user || user.role !== expectedRole) {
    return (
      <>
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {roleLabel} not found
        </div>
        <Link
          to={backPath}
          className="inline-block rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {backLabel}
        </Link>
      </>
    )
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to={backPath}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap"
        >
          ← {backLabel}
        </Link>
        <div className="flex flex-wrap gap-2">
          {!editing && (
            <button
              type="button"
              onClick={() => { setEditing(true); setError(''); setGenMsg('') }}
              disabled={actioning}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
            >
              Edit
            </button>
          )}
          {!user.isEmailVerified && user.email && (
            <button
              type="button"
              onClick={handleVerifyEmail}
              disabled={actioning}
              className="rounded-lg border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
            >
              Verify email
            </button>
          )}
          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={actioning}
            className={`rounded-lg px-4 py-2 text-sm font-medium whitespace-nowrap ${
              user.status === 'ACTIVE'
                ? 'border border-red-600 text-red-600 hover:bg-red-50'
                : 'border border-green-600 text-green-600 hover:bg-green-50'
            } disabled:opacity-50`}
          >
            {actioning ? 'Processing...' : user.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={actioning}
            className="rounded-lg border border-red-700 bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50 whitespace-nowrap"
          >
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {genMsg && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{genMsg}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-xl font-bold text-gray-900">{fullName()}</h1>
          <span
            className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${
              user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {user.status}
          </span>
        </div>

        {editing ? (
          <div className="space-y-4 px-6 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-900">First name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-900">Last name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-900">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-900">Phone</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={actioning}
                className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {actioning ? 'Saving...' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={actioning}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 px-6">
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={user.phoneNumber} />
            <InfoRow label="Role" value={user.role} />
            <InfoRow label="Email Verified" value={user.isEmailVerified ? 'Yes' : 'No'} />
            <InfoRow label="Phone Verified" value={user.isPhoneVerified ? 'Yes' : 'No'} />
            <InfoRow label="Joined" value={formatDate(user.createdAt)} />
            <InfoRow label="Last Updated" value={formatDate(user.updatedAt)} />
          </div>
        )}
      </div>
    </>
  )
}

export default AdminEmploymeUserSection
